/**
 * gift-phone — single validation/normalisation boundary for gift phones
 * (recipient + sender). Shared by GiftFlow, CartDrawer, /cart and the
 * checkout boundary in cartStore so they cannot disagree.
 *
 * Builds on phone-e164.ts (country-scoped, no guessing) and checks the result
 * with libphonenumber-js. Never strips anything except a national leading zero
 * or a dial prefix that matches the selected country. An international number
 * for another country is rejected, never reinterpreted.
 */
import { parsePhoneNumberFromString, type CountryCode as LibCountry } from "libphonenumber-js";
import { latinDigits } from "@/lib/phone-e164";
import { isGiftDestinationActive, type MarketId } from "@/lib/markets";

/**
 * Selectable gift phone countries (the eight supported UI markets) and their
 * calling codes. Kept local to phone handling; must list the same countries as
 * SUPPORTED_MARKET_IDS (guarded by gift-phone tests).
 */
const GIFT_DIAL: Record<string, string> = {
  SA: "966",
  AE: "971",
  KW: "965",
  QA: "974",
  BH: "973",
  OM: "968",
  IQ: "964",
  US: "1",
  CA: "1",
};
export const GIFT_PHONE_COUNTRIES = Object.keys(GIFT_DIAL) as MarketId[];

function isPhoneCountry(c: string | null | undefined): c is MarketId {
  return !!c && GIFT_PHONE_COUNTRIES.includes(c.toUpperCase() as MarketId);
}

export function giftDialCode(country: string | null | undefined): string {
  if (!isPhoneCountry(country)) return "";
  return `+${GIFT_DIAL[country.toUpperCase()]}`;
}

function validNational(digits: string, country: string): boolean {
  if (!digits) return false;
  const p = parsePhoneNumberFromString(digits, country.toUpperCase() as LibCountry);
  return (
    !!p?.isValid() &&
    String(p.nationalNumber) === digits &&
    `+${p.countryCallingCode}` === giftDialCode(country)
  );
}

/**
 * Cleans what the user typed/pasted into the national field for `country`:
 * Arabic/Persian digits → Latin, separators dropped. `+<dial>` / `00<dial>`
 * is stripped only when it matches the selected country (a foreign
 * international number is kept as-is so it fails validation). A bare dial
 * prefix is stripped only when the digits are not already a valid national
 * number (Omani 968xxxxx stays intact). National leading zeroes are dropped.
 */
export function cleanLocalPhoneInput(raw: string, country: string): string {
  const latin = latinDigits(String(raw ?? ""));
  const dial = giftDialCode(country).slice(1);
  let digits = latin.replace(/\D/g, "");
  const intl = /^\s*(\+|00)/.test(latin);
  if (intl) {
    if (!/^\s*\+/.test(latin)) digits = digits.replace(/^00/, "");
    if (dial && digits.startsWith(dial)) return digits.slice(dial.length).replace(/^0+/, "");
    // Mismatched international input: keep the full digits so it is rejected.
    return `+${digits}`;
  }
  const national = digits.replace(/^0+/, "");
  if (dial && !validNational(national, country) && national.startsWith(dial)) {
    const rest = national.slice(dial.length).replace(/^0+/, "");
    if (validNational(rest, country)) return rest;
  }
  return national;
}

/** Returns a libphonenumber-valid E.164 for (local, country) or null. */
export function buildGiftE164(local: string, country: string): string | null {
  if (!isPhoneCountry(country)) return null;
  const cleaned = cleanLocalPhoneInput(local, country);
  if (!cleaned || cleaned.startsWith("+")) return null;
  if (!validNational(cleaned, country)) return null;
  return `${giftDialCode(country)}${cleaned}`;
}

export function isValidGiftPhone(e164: string | null | undefined): boolean {
  if (!e164 || !/^\+\d{8,15}$/.test(e164)) return false;
  const p = parsePhoneNumberFromString(e164);
  return (
    !!p?.isValid() &&
    GIFT_PHONE_COUNTRIES.some((c) => giftDialCode(c) === `+${p.countryCallingCode}`)
  );
}

/** Valid E.164 whose calling code belongs to `country` (same rule GiftFlow applies). */
export function isGiftPhoneFor(
  e164: string | null | undefined,
  country: string | null | undefined,
): boolean {
  if (!e164 || !isPhoneCountry(country) || !isValidGiftPhone(e164)) return false;
  const dial = giftDialCode(country);
  return e164.startsWith(dial) && buildGiftE164(e164.slice(dial.length), country) === e164;
}

/**
 * Splits a saved E.164 into (supported country, national digits) without
 * reinterpreting it. Unknown/foreign numbers return null so callers keep the
 * stored value instead of rewriting it under another country.
 */
export function splitGiftE164(
  e164: string | null | undefined,
): { country: MarketId; local: string } | null {
  if (!isValidGiftPhone(e164)) return null;
  if (!e164) return null;
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return null;
  const preferred = parsed.country as MarketId | undefined;
  const country =
    preferred && isPhoneCountry(preferred) && isGiftPhoneFor(e164, preferred)
      ? preferred
      : GIFT_PHONE_COUNTRIES.find((c) => isGiftPhoneFor(e164, c));
  return country ? { country, local: String(parsed.nationalNumber) } : null;
}

/**
 * Same required validity everywhere: an active recipient destination, a
 * recipient phone valid for that destination, and a valid sender phone in any
 * supported country (sender country is independent).
 */
export function isGiftContactComplete(
  g:
    | {
        recipientPhone?: string;
        senderPhone?: string;
        recipientAddress?: { country?: string } | null;
      }
    | null
    | undefined,
): boolean {
  const c = g?.recipientAddress?.country;
  return !!(
    g &&
    c &&
    isGiftDestinationActive(c) &&
    isPhoneCountry(c) &&
    isGiftPhoneFor(g.recipientPhone, c) &&
    isValidGiftPhone(g.senderPhone)
  );
}
